/** "You are here" kiosk marker: accent badge + pin, Situm style. One per map,
 *  so a plain HTML Marker is fine. (POI markers are an HTML overlay too since
 *  item 0371 -- poiMarkers.js -- and sit BELOW this one in the DOM.)
 *
 *  The colour FOLLOWS the venue's route accent (item 0106). Origin, path and
 *  floor-change dot are one wayfinding object; a venue that brands its route
 *  green would otherwise be left with a single unexplained red badge, and a
 *  marker that no longer matches the line reads as a different kind of thing.
 *  `ink` keeps the label legible when the accent is a pale brand colour --
 *  white-on-yellow is not a label. */
import maplibregl from "maplibre-gl";

import { palette } from "./routeStyles.js";

// How close the label may come to the edge of the map before it is nudged in.
const EDGE_PAD = 8;

export function addYouAreHere(map, kiosk, label, accent) {
  const { accent: bg, ink } = palette(accent);
  const el = document.createElement("div");
  el.style.cssText = "display:flex;flex-direction:column;align-items:center;pointer-events:none;";
  el.innerHTML = `
    <div data-yah-label style="background:${bg};color:${ink};font:700 13px/1 system-ui,sans-serif;
                padding:7px 12px;border-radius:6px;white-space:nowrap;
                box-shadow:0 2px 6px rgba(0,0,0,.35);">${label}</div>
    <div style="width:0;height:0;border-left:7px solid transparent;
                border-right:7px solid transparent;border-top:8px solid ${bg};"></div>
    <div style="width:14px;height:14px;border-radius:50%;background:${bg};
                border:3px solid ${ink};box-shadow:0 1px 4px rgba(0,0,0,.4);margin-top:2px;"></div>
  `;
  const marker = new maplibregl.Marker({ element: el, anchor: "bottom" })
    .setLngLat([kiosk.lng, kiosk.lat])
    .addTo(map);

  // Keep the LABEL inside the map, without moving the pin (item 0151).
  //
  // The marker is centred on the kiosk, and the label is `white-space:nowrap`,
  // so when the kiosk sits near an edge the badge runs past the canvas and the
  // map's overflow clips it -- the visitor sees "Estás aq". Wrapping it would
  // be worse (a two-line badge over the map) and moving the marker would put
  // the "you are here" dot somewhere the visitor is not.
  //
  // So only the badge slides, along its own axis, by as little as it takes.
  // The arrow and the dot stay exactly on the point, which is the part that
  // has to be truthful.
  const badge = el.querySelector("[data-yah-label]");
  const clamp = () => {
    if (!badge || !map.getContainer()) return;
    badge.style.transform = "translateX(0px)";
    const box = badge.getBoundingClientRect();
    const map_ = map.getContainer().getBoundingClientRect();
    let dx = 0;
    if (box.left < map_.left + EDGE_PAD) dx = map_.left + EDGE_PAD - box.left;
    else if (box.right > map_.right - EDGE_PAD) dx = map_.right - EDGE_PAD - box.right;
    // A label wider than the map cannot be made to fit; pin it to the left
    // edge rather than jittering between two impossible constraints.
    if (box.width > map_.width - EDGE_PAD * 2) dx = map_.left + EDGE_PAD - box.left;
    if (dx) badge.style.transform = `translateX(${Math.round(dx)}px)`;
  };

  clamp();
  map.on("move", clamp);
  map.on("resize", clamp);

  // The caller only ever calls remove(); make that detach the listeners too,
  // so a language or accent change (which rebuilds the marker) does not leave
  // a handler running against a dead element.
  const remove = marker.remove.bind(marker);
  marker.remove = () => {
    map.off("move", clamp);
    map.off("resize", clamp);
    return remove();
  };
  return marker;
}
